import React, { useEffect, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { ssoLogin } from '../../Common/Utils/SsoSession';
import useLMSAuth from '../hooks/useLMSAuth';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const PageWrapper = styled.div`
  width: 100%;
  min-height: calc(100vh - 80px);
  background: linear-gradient(135deg, #061b1f 0%, #0e3a44 50%, #0a8294 100%);
  padding: 60px 16px;
  box-sizing: border-box;
  display: flex; align-items: center; justify-content: center;
`;
const Card = styled.div`
  width: 100%; max-width: 960px;
  background: #fff; border-radius: 16px; overflow: hidden;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.35);
  display: grid; grid-template-columns: 1.05fr 1fr;
  animation: ${fadeUp} 0.5s ease both;
  @media (max-width: 860px) { grid-template-columns: 1fr; max-width: 460px; }
`;
const Brand = styled.div`
  background: linear-gradient(160deg, #00292e 0%, #0a8294 100%);
  color: #fff; padding: 56px 44px;
  display: flex; flex-direction: column; justify-content: space-between;
  position: relative; overflow: hidden;
  &::before {
    content: ''; position: absolute; top: -120px; right: -120px;
    width: 320px; height: 320px;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.18), transparent 70%);
    border-radius: 50%;
  }
  @media (max-width: 860px) { padding: 36px 28px; }
`;
const BrandLogo = styled.div`display:flex;align-items:center;gap:10px;`;
const LogoIcon = styled.div`
  width: 40px; height: 40px; border-radius: 10px;
  background: linear-gradient(135deg, #38bdf8, #0ea5e9);
  display:flex;align-items:center;justify-content:center;
  font-weight:900;letter-spacing:-1px;font-size:1.05rem;
`;
const BrandTitle = styled.h2`font-size:2rem;font-weight:900;margin:28px 0 12px;letter-spacing:-0.5px;line-height:1.2;`;
const BrandDesc = styled.p`font-size:0.95rem;opacity:0.85;line-height:1.65;margin:0;`;
const BrandList = styled.ul`
  list-style:none;padding:0;margin:28px 0 0;display:grid;gap:10px;
  li{display:flex;gap:10px;font-size:0.9rem;opacity:0.9;align-items:center;}
  li::before{content:'✓';color:#67e8f9;font-weight:800;}
`;
const Form = styled.form`
  padding: 56px 48px;
  display: flex; flex-direction: column; justify-content: center;
  @media (max-width: 860px) { padding: 36px 28px; }
`;
const FormTitle = styled.h1`font-size:1.6rem;font-weight:900;margin:0 0 6px;color:#0f172a;letter-spacing:-0.4px;`;
const FormSub = styled.p`margin:0 0 24px;color:#64748b;font-size:0.92rem;line-height:1.55;`;

const Field = styled.div`margin-bottom: 14px;`;
const InputWrap = styled.div`
  position: relative;
  svg { position: absolute; left: 16px; top: 50%; transform: translateY(-50%); width: 18px; height: 18px; color: #94a3b8; }
`;
const Input = styled.input`
  width: 100%; padding: 14px 14px 14px 46px;
  border: 1px solid #e2e8f0; border-radius: 10px;
  font-size: 0.95rem; box-sizing: border-box;
  transition: border-color 0.15s, box-shadow 0.15s;
  &:focus { outline: none; border-color: #0891b2; box-shadow: 0 0 0 3px rgba(8,145,178,0.15); }
`;
const Submit = styled.button`
  width: 100%;
  padding: 15px;
  margin-top: 6px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(135deg, #0891b2, #0e7490);
  color: #fff;
  font-size: 1rem;
  font-weight: 800;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;
  box-shadow: 0 6px 16px rgba(8, 145, 178, 0.25);
  &:hover { transform: translateY(-1px); box-shadow: 0 10px 24px rgba(8,145,178,0.35); }
  &:disabled { opacity: 0.6; cursor: not-allowed; transform: none; box-shadow: none; }
`;
const SsoBadge = styled.div`
  margin-top: 20px; padding: 12px 14px;
  background: #ecfeff; border: 1px solid #a5f3fc;
  border-radius: 10px; color: #0e7490; font-size: 0.83rem;
  display: flex; gap: 8px; align-items: flex-start; line-height: 1.55;
  svg { width: 16px; height: 16px; flex-shrink: 0; margin-top: 1px; }
  strong { display: block; margin-bottom: 4px; font-size: 0.88rem; }
`;
const Foot = styled.div`
  display:flex;justify-content:space-between;gap:12px;margin-top:18px;
  font-size:0.86rem;
  a{color:#0891b2;font-weight:600;text-decoration:none;}
  a:hover{text-decoration:underline;}
`;
const ErrorMsg = styled.div`
  background:#fef2f2;border:1px solid #fecaca;color:#b91c1c;
  border-radius:10px;padding:10px 14px;font-size:0.88rem;margin-bottom:14px;
`;
const SyncingMsg = styled.div`
  background:#f0f9ff;border:1px solid #bae6fd;color:#075985;
  border-radius:10px;padding:14px 16px;font-size:0.9rem;margin-bottom:14px;
  display:flex;gap:10px;align-items:center;
`;

export default function LoginPage() {
  const history = useHistory();
  const loc = useLocation();
  const { login, loading } = useLMSAuth();
  const [form, setForm] = useState({ id: '', pw: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const next = new URLSearchParams(loc.search).get('next') || '/MyClassroom';

  // 이미 로그인이면 next로 (다른 SSO 서비스에서 로그인된 경우 자동)
  useEffect(() => {
    if (!loading && login) history.replace(next);
  }, [loading, login, history, next]);

  const submit = async (e) => {
    e?.preventDefault?.();
    setError('');
    if (!form.id) return setError('아이디를 입력하세요.');
    if (!form.pw) return setError('비밀번호를 입력하세요.');
    setBusy(true);
    try {
      const r = await ssoLogin(form.id, form.pw);
      if (r && r.success) {
        // 통합 세션 쿠키가 .antsnest.co.kr 도메인으로 설정됨
        // → 모든 SSO 서비스에서 동시 로그인 상태
        window.location.href = next;
      } else {
        setError(r?.message || '아이디 또는 비밀번호가 올바르지 않습니다.');
      }
    } catch (err) {
      setError('로그인 중 오류가 발생했습니다.');
    } finally {
      setBusy(false);
    }
  };
  const onKey = (e) => { if (e.key === 'Enter') submit(); };

  return (
    <PageWrapper>
      <Card>
        <Brand>
          <BrandLogo>
            <LogoIcon>AN</LogoIcon>
            <div style={{ fontWeight: 900, fontSize: '1.05rem' }}>ANTSNEST LMS</div>
          </BrandLogo>
          <div>
            <BrandTitle>실무에 가장 가까운<br />기업용 학습 플랫폼</BrandTitle>
            <BrandDesc>강사가 설계한 프로젝트 중심 커리큘럼으로 빠르게 성장하세요.</BrandDesc>
            <BrandList>
              <li>관리자 승인 후 강의 시청</li>
              <li>강의 일정·자료 한 곳에서 관리</li>
              <li>한 번 로그인으로 모든 앤츠네스트 서비스 이용</li>
            </BrandList>
          </div>
          <div style={{ fontSize: '0.78rem', opacity: 0.6 }}>© ANTSNEST DEVELOPER CENTER</div>
        </Brand>

        <Form onSubmit={submit}>
          <FormTitle>앤츠네스트 통합 로그인</FormTitle>
          <FormSub>다른 앤츠네스트 서비스에 이미 로그인되어 있다면 자동으로 인증됩니다.</FormSub>

          {loading && (
            <SyncingMsg>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" strokeDasharray="40 20" strokeLinecap="round">
                  <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite" />
                </circle>
              </svg>
              SSO 세션 확인 중...
            </SyncingMsg>
          )}

          {error && <ErrorMsg>{error}</ErrorMsg>}

          <Field>
            <InputWrap>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              <Input type="text" placeholder="아이디" autoComplete="username"
                value={form.id} onChange={(e) => setForm({ ...form, id: e.target.value })} onKeyDown={onKey} />
            </InputWrap>
          </Field>
          <Field>
            <InputWrap>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <Input type="password" placeholder="비밀번호" autoComplete="current-password"
                value={form.pw} onChange={(e) => setForm({ ...form, pw: e.target.value })} onKeyDown={onKey} />
            </InputWrap>
          </Field>

          <Submit type="submit" disabled={busy}>{busy ? '로그인 중...' : '로그인'}</Submit>

          <SsoBadge>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 8v4M12 16h.01" />
            </svg>
            <div>
              <strong>SSO 통합 로그인</strong>
              한 번의 로그인으로 talk · store · estimate · brand 등 모든 앤츠네스트 서비스에서 사용 가능합니다.
            </div>
          </SsoBadge>

          <Foot>
            <Link to="/FindMemberInfo">아이디·비밀번호 찾기</Link>
            <Link to="/MemberJoin">회원가입 →</Link>
          </Foot>
        </Form>
      </Card>
    </PageWrapper>
  );
}
